import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import LangToggle from "@/components/LangToggle";
import { ShieldCheck, Lock, Database, LogOut, Building2, MapPin, Phone, Mail, Check, Loader2, Upload, Image as ImageIcon, Trash2 } from "lucide-react";

export default function Settings() {
  const { user, t, logout } = useAuth();
  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-pine">{t.nav.settings}</div>
        <h1 className="mt-2 font-heading text-3xl tracking-tight">{t.settings.title}</h1>
      </div>

      <Section title={t.settings.account}>
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full" /> : <div className="w-14 h-14 rounded-full bg-pine-50 text-pine flex items-center justify-center font-heading text-xl">{user.name?.[0]}</div>}
          <div>
            <div className="font-medium">{user.name}</div>
            <div className="text-sm text-ink-muted">{user.email}</div>
          </div>
        </div>
      </Section>

      <LogoSection t={t} />
      <PracticeSection t={t} />

      <Section title={t.settings.language}>
        <LangToggle />
      </Section>

      <Section title={t.settings.privacy} icon={<ShieldCheck className="w-4 h-4" />}>
        <p className="text-sm text-ink-muted leading-relaxed">{t.settings.privacyDesc}</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Badge icon={<Lock className="w-4 h-4" />} label="AES-256" />
          <Badge icon={<Database className="w-4 h-4" />} label="EU-Hosting" />
        </div>
      </Section>

      <Section title={t.settings.retention}>
        <p className="text-sm text-ink-muted leading-relaxed">{t.settings.retentionDesc}</p>
      </Section>

      <button
        onClick={logout}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-hairline hover:bg-pine-50 transition text-sm"
        data-testid="settings-logout-btn"
      >
        <LogOut className="w-4 h-4" /> {t.settings.logout}
      </button>
    </div>
  );
}

function PracticeSection({ t }) {
  const [form, setForm] = useState({
    practice_name: "",
    practice_address: "",
    practice_phone: "",
    practice_email: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/practice`).then((r) => {
      setForm({
        practice_name: r.data.practice_name || "",
        practice_address: r.data.practice_address || "",
        practice_phone: r.data.practice_phone || "",
        practice_email: r.data.practice_email || "",
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/practice`, form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-hairline rounded-2xl p-6" data-testid="practice-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-pine">
            <Building2 className="w-4 h-4" /> {t.settings.practice}
          </div>
          <p className="mt-2 text-sm text-ink-muted">{t.settings.practiceDesc}</p>
        </div>
        {saved && (
          <motion.div
            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pine-50 text-pine text-xs font-semibold"
          >
            <Check className="w-3.5 h-3.5" /> {t.settings.practiceSaved}
          </motion.div>
        )}
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <PracticeField
          icon={<Building2 className="w-4 h-4" />}
          label={t.settings.practiceName}
          placeholder={t.settings.practiceNamePh}
          value={form.practice_name}
          onChange={(v) => setForm({ ...form, practice_name: v })}
          testid="practice-name"
          disabled={loading}
        />
        <PracticeField
          icon={<MapPin className="w-4 h-4" />}
          label={t.settings.practiceAddress}
          placeholder={t.settings.practiceAddressPh}
          value={form.practice_address}
          onChange={(v) => setForm({ ...form, practice_address: v })}
          testid="practice-address"
          disabled={loading}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PracticeField
            icon={<Phone className="w-4 h-4" />}
            label={t.settings.practicePhone}
            placeholder={t.settings.practicePhonePh}
            value={form.practice_phone}
            onChange={(v) => setForm({ ...form, practice_phone: v })}
            testid="practice-phone"
            disabled={loading}
          />
          <PracticeField
            icon={<Mail className="w-4 h-4" />}
            label={t.settings.practiceEmail}
            placeholder={t.settings.practiceEmailPh}
            value={form.practice_email}
            onChange={(v) => setForm({ ...form, practice_email: v })}
            testid="practice-email"
            type="email"
            disabled={loading}
          />
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={saving || loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover disabled:opacity-60 transition"
            data-testid="practice-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {t.settings.practiceSave}
          </button>
        </div>
      </form>
    </div>
  );
}

function PracticeField({ icon, label, placeholder, value, onChange, testid, type = "text", disabled }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">{label}</label>
      <div className="relative mt-2">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none">
          {icon}
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none text-sm disabled:opacity-60"
          data-testid={`field-${testid}`}
        />
      </div>
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="bg-surface border border-hairline rounded-2xl p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-pine">
        {icon} {title}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Badge({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-pine-50 text-pine text-sm">
      {icon} {label}
    </div>
  );
}

function LogoSection({ t }) {
  const [hasLogo, setHasLogo] = useState(false);
  const [preview, setPreview] = useState(null); // object URL for local preview
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cacheBust, setCacheBust] = useState(0);
  const fileRef = useRef(null);

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/practice/logo/status`);
      setHasLogo(!!r.data.has_logo);
    } catch { setHasLogo(false); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);

  const onSelect = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert("Max 2 MB");
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      alert("PNG, JPEG oder WebP nur");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      await axios.post(`${API}/practice/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setHasLogo(true);
      setPreview(null);
      setCacheBust((n) => n + 1);
    } catch (e) {
      alert("Upload failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    if (!window.confirm("?")) return;
    try {
      await axios.delete(`${API}/practice/logo`);
      setHasLogo(false);
      setPreview(null);
    } catch (e) { alert(e.message); }
  };

  const logoUrl = hasLogo ? `${API}/practice/logo?t=${cacheBust}` : null;

  return (
    <div className="bg-surface border border-hairline rounded-2xl p-6" data-testid="logo-section">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-pine">
        <ImageIcon className="w-4 h-4" /> {t.settings.logo}
      </div>
      <p className="mt-2 text-sm text-ink-muted">{t.settings.logoDesc}</p>

      <div className="mt-5 flex items-center gap-6 flex-wrap">
        <div className="w-40 h-24 rounded-xl bg-bone border border-hairline flex items-center justify-center overflow-hidden">
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-ink-muted" />
          ) : preview ? (
            <img src={preview} alt="" className="max-w-full max-h-full object-contain" />
          ) : logoUrl ? (
            <img
              src={logoUrl}
              alt="Practice logo"
              className="max-w-full max-h-full object-contain"
              crossOrigin="use-credentials"
              data-testid="logo-preview"
            />
          ) : (
            <div className="text-center text-xs text-ink-muted px-3">{t.settings.logoNone}</div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => onSelect(e.target.files?.[0])}
            data-testid="logo-file-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover disabled:opacity-60 transition"
            data-testid="logo-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? t.settings.logoUploading : (hasLogo ? t.settings.logoReplace : t.settings.logoUpload)}
          </button>
          {hasLogo && (
            <button
              type="button"
              onClick={removeLogo}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-hairline text-sm text-terracotta hover:bg-terracotta/10 transition"
              data-testid="logo-remove-btn"
            >
              <Trash2 className="w-4 h-4" /> {t.settings.logoRemove}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
